import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Check, X, Sparkles, RotateCcw } from "lucide-react";
import { getQuiz } from "../api";
import { recordMemoryScore } from "../lib/storage";
import { track } from "../lib/analytics";
import { happyMoment } from "../lib/upgrade";

// Reading Memory Score: 3 quick comprehension questions after a chapter.
// Questions are pre-generated server-side and cached (zero live AI cost).
export default function MemoryCheck({ book, chapter, bookName }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | active | done | error
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [answers, setAnswers] = useState([]);

  const start = async () => {
    setPhase("loading");
    try {
      const data = await getQuiz(book, chapter);
      const qs = (data.questions || []).slice(0, 3);
      if (!qs.length) throw new Error("empty");
      setQuestions(qs);
      setStep(0);
      setPicked(null);
      setCorrect(0);
      setAnswers([]);
      setPhase("active");
    } catch {
      setPhase("error");
    }
  };

  const choose = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const isRight = i === questions[step].answer;
    if (isRight) setCorrect((c) => c + 1);
    setAnswers((a) => [...a, { picked: i, correct: isRight }]);
  };

  const next = () => {
    if (step + 1 < questions.length) {
      setStep((s) => s + 1);
      setPicked(null);
    } else {
      const finalCorrect = correct;
      recordMemoryScore(book, chapter, finalCorrect, questions.length);
      track("quiz_complete", { book, chapter, score: finalCorrect, total: questions.length });
      setPhase("done");
      if (finalCorrect >= Math.ceil(questions.length / 2)) happyMoment();
    }
  };

  const retry = () => {
    setStep(0);
    setPicked(null);
    setCorrect(0);
    setAnswers([]);
    setPhase("active");
  };

  if (phase === "idle") {
    return (
      <button
        onClick={start}
        data-testid="memory-check-start"
        className="font-ui group mx-auto mb-10 inline-flex items-center gap-2 rounded-full border border-primary/50 bg-background/90 px-6 py-3 text-sm font-semibold text-foreground shadow-lg backdrop-blur-md transition-all hover:bg-background"
      >
        <Brain className="h-4 w-4 text-primary" />
        Test your memory — 3 quick questions
      </button>
    );
  }

  if (phase === "loading") {
    return (
      <div data-testid="memory-check-loading" className="mx-auto mb-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Sparkles className="h-4 w-4 animate-pulse text-primary" />
        Preparing your memory check…
      </div>
    );
  }

  if (phase === "error") {
    return (
      <button
        onClick={start}
        data-testid="memory-check-retry-error"
        className="font-ui mx-auto mb-10 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <RotateCcw className="h-4 w-4" />
        Couldn't load questions — tap to try again
      </button>
    );
  }

  if (phase === "done") {
    const total = questions.length;
    const pct = Math.round((correct / total) * 100);
    const msg =
      pct === 100 ? "Perfect recall! Your tree drinks deep." :
      pct >= 67 ? "Strong memory — well read." :
      pct >= 34 ? "A good start. Revisit the chapter to grow." :
      "Worth another read — let it take root.";
    return (
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="memory-check-result"
        className="mx-auto mb-10 max-w-xl rounded-3xl border border-primary/30 bg-background/85 p-8 text-center shadow-lg backdrop-blur-md"
      >
        <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          Reading Memory Score
        </p>
        <p data-testid="memory-check-score" className="font-serif-display text-5xl font-semibold text-foreground">
          {correct}<span className="text-2xl text-muted-foreground">/{total}</span>
        </p>
        <p className="reading-text mt-3 text-foreground/90">{msg}</p>
        <button
          onClick={retry}
          data-testid="memory-check-redo"
          className="font-ui mt-6 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Try again
        </button>
      </motion.div>
    );
  }

  // active
  const q = questions[step];
  return (
    <div
      data-testid="memory-check-active"
      className="mx-auto mb-10 max-w-xl rounded-3xl border border-border bg-background/80 p-8 text-left shadow-lg backdrop-blur-md"
    >
      <div className="mb-5 flex items-center justify-between">
        <p className="font-ui text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          Memory check
        </p>
        <p className="font-ui text-xs text-muted-foreground">
          {step + 1} / {questions.length}
        </p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.3 }}
        >
          <p className="reading-text font-serif-display mb-5 text-xl leading-relaxed">{q.q}</p>
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const isPicked = picked === i;
              const isAnswer = i === q.answer;
              const reveal = picked !== null;
              let cls = "border-border bg-background/40 hover:border-primary/50";
              if (reveal && isAnswer) cls = "border-emerald-500/60 bg-emerald-500/15 text-foreground";
              else if (reveal && isPicked && !isAnswer) cls = "border-red-500/60 bg-red-500/15 text-foreground";
              else if (reveal) cls = "border-border bg-background/40 opacity-60";
              return (
                <button
                  key={i}
                  onClick={() => choose(i)}
                  disabled={reveal}
                  data-testid={`memory-check-option-${i}`}
                  className={`font-ui flex w-full items-center justify-between gap-3 rounded-2xl border px-5 py-4 text-left text-sm transition-all ${cls}`}
                >
                  <span>{opt}</span>
                  {reveal && isAnswer && <Check className="h-4 w-4 shrink-0 text-emerald-500" />}
                  {reveal && isPicked && !isAnswer && <X className="h-4 w-4 shrink-0 text-red-500" />}
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {picked !== null && (
        <button
          onClick={next}
          data-testid="memory-check-next"
          className="font-ui mt-6 w-full rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
        >
          {step + 1 < questions.length ? "Next question" : "See my score"}
        </button>
      )}
    </div>
  );
}
